import React, { useCallback, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { AnimatedRoundedSelectSize } from "../../enums/AnimatedRoundedSelectSize";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import AnimatedSelectSearchInput from "./AnimatedSelectSearchInput";
import DropdownIndicator from "./DropdownIndicator";

const ANIMATION_TIME = 500;

interface Props {
  size?: AnimatedRoundedSelectSize;
  placeholder: string;
  leftIcon?: JSX.Element;
  value: string;
  options: { value: string; label: string; leftIcon?: JSX.Element }[];
  onChange: (value: string) => void;
  showValueIfSelected?: boolean;
  centerValue?: boolean;
  nonScrollableOptionBox?: boolean;
  disabled?: boolean;
  className?: string;
  isSearchable?: boolean;
  hasError?: boolean;
  distinctPlaceholder?: boolean;
}

const PIXEL_VALUES_FOR_SIZE = {
  [AnimatedRoundedSelectSize.ExtraSmall]: 32,
  [AnimatedRoundedSelectSize.Small]: 44,
  [AnimatedRoundedSelectSize.Medium]: 48,
};

const AnimatedRoundedSelect = ({
  size = AnimatedRoundedSelectSize.Medium,
  placeholder,
  leftIcon,
  options,
  value,
  onChange,
  showValueIfSelected = true,
  centerValue = false,
  nonScrollableOptionBox = false,
  disabled = false,
  isSearchable = false,
  className,
  hasError,
  distinctPlaceholder = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animationInProgress, setAnimationInProgress] = useState(false);

  const selectRef = useRef<HTMLDivElement>(null);
  //TODO: Remove as any after proper usage of themes is implemented
  const theme = useTheme();
  const animationTimeoutRef = useRef<number>();
  const isOpenRef = useRef(isOpen);
  const animationInProgressRef = useRef(animationInProgress);
  const [searchValue, setSearchValue] = useState("");

  const changeOpenState = useCallback(() => {
    if (disabled) return;
    setSearchValue("");
    animationInProgressRef.current = true;
    setAnimationInProgress(true);
    isOpenRef.current = !isOpen;
    setIsOpen(!isOpen);
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    animationTimeoutRef.current = setTimeout(() => {
      animationInProgressRef.current = false;
      setAnimationInProgress(false);
    }, ANIMATION_TIME);
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isOpen, disabled]);

  useOutsideClick(selectRef, () => {
    if (isOpenRef.current) changeOpenState();
  });

  const selectOption = useCallback(
    (value: string) => {
      onChange(value);
      setSearchValue("");
    },
    [onChange]
  );

  const selectedOption = useMemo(() => {
    return options.find((o) => o.value === value);
  }, [options, value]);

  const valueToShow = useMemo(() => {
    if (!!value) {
      return showValueIfSelected && !!selectedOption?.label
        ? "label"
        : "placeholder";
    }
    return "placeholder";
  }, [value, showValueIfSelected, selectedOption]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, isSearchable, searchValue]);

  return (
    <MainContainer
      ref={selectRef}
      size={size}
      onClick={() => changeOpenState()}
      className={className}
    >
      <AbsoluteContainer
        size={size}
        isOpen={isOpen}
        animationInProgress={animationInProgress}
        disabled={disabled}
      >
        <ValueContainer
          size={size}
          animationInProgress={animationInProgress}
          isOpen={isOpen}
          centerValue={centerValue}
          onClick={() => changeOpenState()}
        >
          <LeftContainer centerValue={centerValue}>
            {!isSearchable ? (
              <>
                {leftIcon && <IconContainer>{leftIcon}</IconContainer>}
                {valueToShow === "label" && (
                  <Value>{selectedOption?.label}</Value>
                )}
                {valueToShow === "placeholder" && (
                  <Placeholder distinctPlaceholder={distinctPlaceholder}>
                    {placeholder}
                  </Placeholder>
                )}
              </>
            ) : (
              <AnimatedSelectSearchInput
                searchValue={searchValue}
                placeholder={placeholder}
                selectedValue={selectedOption?.label}
                onSearchChange={setSearchValue}
                isOpen={isOpen}
                hasError={hasError}
                distinctPlaceholder={distinctPlaceholder}
                leftIcon={leftIcon}
              />
            )}
          </LeftContainer>
          <IndicatorContainer isOpen={isOpen}>
            <DropdownIndicator
              animationTime={ANIMATION_TIME}
              color={theme.redesign.defaultColor}
              pointUp={isOpen}
            />
          </IndicatorContainer>
        </ValueContainer>
        <OptionsContainer
          animationInProgress={animationInProgress}
          isOpen={isOpen}
          numberOfOptions={options.length}
          nonScrollableOptionBox={nonScrollableOptionBox}
        >
          {filteredOptions.map((v, i) => (
            <Option
              key={i}
              isSelected={value === v.value}
              onClick={() => selectOption(v.value)}
            >
              {!!v.leftIcon && v.leftIcon}
              {v.label}
            </Option>
          ))}
        </OptionsContainer>
      </AbsoluteContainer>
    </MainContainer>
  );
};

const Value = styled.span<{ hasError?: boolean }>`
  color: ${({ theme, hasError }) =>
    hasError ? theme.redesign.errorColor : theme.redesign.defaultColor};
`;

const Placeholder = styled.span<{
  distinctPlaceholder?: boolean;
  hasError?: boolean;
}>`
  color: ${({ theme, distinctPlaceholder, hasError }) =>
    hasError
      ? theme.redesign.errorColor
      : distinctPlaceholder
      ? theme.redesign.form.textField.placeholderColor
      : theme.redesign.defaultColor};
`;

const IndicatorContainer = styled.div<{ isOpen: boolean }>`
  ${({ isOpen }) => (isOpen ? "margin-bottom: -4px;" : "margin-bottom: 4px;")}
  transition: margin-bottom ${ANIMATION_TIME}ms;
`;

const IconContainer = styled.div`
  padding-right: 8px;
  display: flex;
  align-items: center;
`;

const LeftContainer = styled.div<{ centerValue: boolean }>`
  display: flex;
  align-items: center;
  ${({ centerValue }) => centerValue && `padding-right: 10px;`}
  height: 100%;
  width: 100%;
`;

const MainContainer = styled.div<{ size: AnimatedRoundedSelectSize }>`
  font-size: 14px;
  display: flex;
  position: relative;
  min-height: ${({ size }) => `${PIXEL_VALUES_FOR_SIZE[size]}px`};
  width: 100%;
  cursor: pointer;
  font-family: ${({ theme }) => theme.redesign.regularFont};
`;

const AbsoluteContainer = styled.div<{
  size: AnimatedRoundedSelectSize;
  isOpen: boolean;
  animationInProgress: boolean;
  disabled?: boolean;
}>`
  background: ${({ theme, disabled }) =>
    disabled
      ? theme.redesign.darkBackground
      : theme.redesign.defaultBackground};
  border: 1px solid ${({ theme }) => theme.redesign.defaultColor};
  position: absolute;
  top: 0;
  width: 100%;
  overflow: hidden;
  z-index: 3;
  border-radius: ${({ size, theme }) =>
    theme.redesign.form.animatedRoundedSelect.borderRadiusPerSize[size]};
`;

const ValueContainer = styled.div<{
  size: AnimatedRoundedSelectSize;
  isOpen: boolean;
  animationInProgress: boolean;
  centerValue: boolean;
}>`
  display: flex;
  align-items: center;
  height: ${({ size }) => `${PIXEL_VALUES_FOR_SIZE[size] - 2}px`};
  margin: 0 20px;
  ${({ isOpen, animationInProgress, theme }) =>
    (isOpen || animationInProgress) &&
    `border-bottom: 1px solid ${theme.redesign.defaultColor};`}
  justify-content: ${({ centerValue }) =>
    centerValue ? "center" : "space-between"};
`;

const OptionsContainer = styled.div<{
  isOpen: boolean;
  animationInProgress: boolean;
  nonScrollableOptionBox: boolean;
  numberOfOptions: number;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: ${({ isOpen, nonScrollableOptionBox, numberOfOptions }) =>
    isOpen
      ? nonScrollableOptionBox
        ? `${numberOfOptions * 32}px`
        : "256px"
      : "0px"};
  overflow: ${({ isOpen, animationInProgress }) =>
    isOpen && !animationInProgress ? "auto" : "hidden"};
  transition: max-height ${ANIMATION_TIME}ms;
`;

const Option = styled.div<{ isSelected: boolean }>`
  min-height: 32px;
  display: block;
  line-height: 32px;
  width: calc(100% - 40px);
  padding: 0 20px;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  color: ${({ isSelected, theme }) =>
    isSelected
      ? theme.redesign.form.animatedRoundedSelect.optionLabelColor.selected
      : theme.redesign.form.animatedRoundedSelect.optionLabelColor.default};
  align-items: center;
  background: ${({ isSelected, theme }) =>
    isSelected
      ? theme.redesign.form.animatedRoundedSelect.selectedOptionBackground
      : theme.redesign.defaultBackground};
  &:hover {
    ${({ isSelected, theme }) =>
      !isSelected &&
      `background: ${theme.redesign.form.animatedRoundedSelect.hoveredOptionBackground};
        color: ${theme.redesign.form.animatedRoundedSelect.optionLabelColor.hover};
      `}
  }
  }
`;

export default AnimatedRoundedSelect;
